import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { actionsApi, activityApi } from '../api';

// ─── KONSTANTEN ────────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  CRITICAL: { icon: 'Kritisch', color: '#ef4444', bg: '#450a0a', badge: '#ef4444' },
  HIGH:     { icon: 'Hoch',     color: '#f97316', bg: '#431407', badge: '#f97316' },
  NORMAL:   { icon: 'Normal',   color: '#6366f1', bg: '#1e1b4b', badge: '#6366f1' },
  LOW:      { icon: 'Niedrig',  color: '#22c55e', bg: '#052e16', badge: '#22c55e' },
};

const SOURCE_BADGE = {
  RADAR:     { label: 'Radar',     color: '#06b6d4' },
  INBOX:     { label: 'Posteingang', color: '#8b5cf6' },
  SAVINGS:   { label: 'Optimierung', color: '#22c55e' },
  COMPARE:   { label: 'Vergleich', color: '#f59e0b' },
  KUENDIGUNG:{ label: 'Kündigung', color: '#ef4444' },
  DOCUMENT:  { label: 'Dokument',  color: '#94a3b8' },
  MANUAL:    { label: 'Manuell',   color: '#64748b' },
  SYSTEM:    { label: 'System',    color: '#475569' },
};

const STATUS_LABELS = {
  OPEN:      'Offen',
  SNOOZED:   'Verschoben',
  COMPLETED: 'Erledigt',
  DISMISSED: 'Verworfen',
};

// ─── HILFS-STYLES ─────────────────────────────────────────────────────────────

const card = {
  background: '#1e293b', borderRadius: 12, padding: '14px 16px',
  border: '1px solid #334155', marginBottom: 8,
};

const badge = (color) => ({
  display: 'inline-block', background: `${color}22`, color,
  borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600,
});

// ─── SUBKOMPONENTEN ───────────────────────────────────────────────────────────

function SummaryBar({ summary }) {
  if (!summary) return null;
  const items = [
    { label: 'Kritisch',    value: summary.critical, color: '#ef4444' },
    { label: 'Hoch',        value: summary.high,     color: '#f97316' },
    { label: 'Heute',       value: summary.today,    color: '#3b82f6' },
    { label: 'Überfällig',  value: summary.overdue,  color: '#dc2626' },
    { label: 'Diese Woche', value: summary.thisWeek, color: '#f59e0b' },
    { label: 'Gesamt offen',value: summary.open,     color: '#94a3b8' },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
      {items.map(item => (
        <div key={item.label} style={{
          background: '#1e293b', border: `1px solid ${item.value > 0 ? item.color : '#334155'}`,
          borderRadius: 8, padding: '8px 14px', textAlign: 'center', minWidth: 80,
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: item.value > 0 ? item.color : '#475569' }}>
            {item.value}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

function ActionCard({ action, onComplete, onDismiss, onSnooze, onReopen, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [showSnooze, setShowSnooze] = useState(false);
  const pc = PRIORITY_CONFIG[action.priority] || PRIORITY_CONFIG.NORMAL;
  const sb = SOURCE_BADGE[action.source] || SOURCE_BADGE.MANUAL;

  const tomorrow = () => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };

  const dueStr = action.due_at ? action.due_at.slice(0, 10) : null;
  const today  = new Date().toISOString().slice(0, 10);
  const isOverdue = dueStr && dueStr < today;

  return (
    <div style={{ ...card, borderLeft: `3px solid ${pc.color}`, background: pc.bg }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* Prioritäts-Indikator */}
        <div style={{ marginTop: 2 }}>
          <span style={badge(pc.color)}>{pc.icon}</span>
        </div>

        {/* Inhalt */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{action.title}</span>
            <span style={badge(sb.color)}>{sb.label}</span>
            {action.vertrag_titel && (
              <span style={{ color: '#94a3b8', fontSize: 12 }}>{action.vertrag_titel}</span>
            )}
          </div>

          {action.description && (
            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{action.description}</div>
          )}

          {dueStr && (
            <div style={{ color: isOverdue ? '#ef4444' : '#f59e0b', fontSize: 12, marginTop: 4 }}>
              {isOverdue ? 'Überfällig: ' : 'Fällig: '}{dueStr}
            </div>
          )}

          {/* Snooze-Datumsauswahl */}
          {showSnooze && (
            <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={snoozeDate || tomorrow()}
                min={tomorrow()}
                onChange={e => setSnoozeDate(e.target.value)}
                style={{ background: '#0f172a', border: '1px solid #475569', color: '#f1f5f9',
                  borderRadius: 6, padding: '4px 8px', fontSize: 12 }} />
              <button className="btn-secondary" onClick={() => {
                onSnooze(action.id, snoozeDate || tomorrow());
                setShowSnooze(false);
              }}>Verschieben</button>
              <button className="btn-secondary" onClick={() => setShowSnooze(false)}>Abbruch</button>
            </div>
          )}
        </div>

        {/* Aktions-Buttons */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
          {(action.status === 'OPEN' || (action.status === 'SNOOZED')) && (
            <>
              <button className="btn-primary" onClick={() => onComplete(action.id)}>Erledigt</button>
              <button className="btn-secondary" onClick={() => { setShowSnooze(!showSnooze); setExpanded(false); }}>Später</button>
              {action.action_url && (
                <button className="btn-secondary" onClick={() => onNavigate(action.action_url)}>Öffnen</button>
              )}
            </>
          )}
          {action.status === 'COMPLETED' && (
            <button className="btn-secondary" onClick={() => onReopen(action.id)}>Wieder öffnen</button>
          )}
          <button className="btn-ghost"
            onClick={() => setExpanded(!expanded)} title="Mehr Optionen">
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Erweitertes Menü */}
      {expanded && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, paddingLeft: 4 }}>
          {action.status !== 'DISMISSED' && (
            <button className="btn-secondary" onClick={() => { onDismiss(action.id); setExpanded(false); }}>Verwerfen</button>
          )}
          <span style={{ color: '#475569', fontSize: 11, alignSelf: 'center' }}>
            {STATUS_LABELS[action.status] || action.status} · Quelle: {action.source}
          </span>
        </div>
      )}
    </div>
  );
}

function ActivityTab({ activity, loading }) {
  if (loading) return <div style={{ color: '#94a3b8', padding: 20 }}>Lade Aktivitäten...</div>;
  if (!activity) return <div style={{ color: '#94a3b8', padding: 20 }}>Keine Daten verfügbar.</div>;

  const items = [
    { label: 'Neue Dokumente im Posteingang', value: activity.newInboxItems, url: '/posteingang' },
    { label: 'Automatisch zugeordnete Dokumente', value: activity.autoAssigned, url: '/posteingang' },
    { label: 'Neue Handlungsempfehlungen', value: activity.newActions, url: '/actions' },
    { label: 'Preisänderungen erkannt', value: activity.priceChanges, url: '/vertraege' },
  ];

  const hasActivity = items.some(i => i.value > 0);

  return (
    <div>
      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 12 }}>
        Seit Ihrem letzten Besuch ({activity.since ? activity.since.slice(0, 10) : '–'}):
      </div>
      {!hasActivity ? (
        <div style={{ ...card, color: '#94a3b8' }}>Keine neuen Aktivitäten seit dem letzten Besuch.</div>
      ) : (
        items.filter(i => i.value > 0).map(item => (
          <div key={item.label} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#f1f5f9', fontSize: 14 }}>{item.label}</span>
            <span style={{ color: '#6366f1', fontWeight: 700, fontSize: 20 }}>{item.value}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ─── HAUPTSEITE ────────────────────────────────────────────────────────────────

export default function ActionsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('open'); // open | completed | dismissed | activity
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actLoading, setActLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterSource, setFilterSource] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterPriority) params.priority = filterPriority;
      if (filterSource) params.source = filterSource;

      // Tab-basierter Status-Filter
      if (tab === 'completed') params.status = 'COMPLETED';
      else if (tab === 'dismissed') params.status = 'DISMISSED';
      // 'open': kein status-filter → Backend gibt OPEN + aktive SNOOZED zurück

      const [actRes, sumRes] = await Promise.all([
        actionsApi.list(params),
        actionsApi.summary(),
      ]);
      setActions(actRes.actions || []);
      setSummary(sumRes);
    } catch {
      setError('Actions konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [tab, filterPriority, filterSource]);

  useEffect(() => { load(); }, [load]);

  // Activity-Tab: last_seen_at aktualisieren und Aktivitäten laden
  useEffect(() => {
    if (tab !== 'activity') return;
    setActLoading(true);
    activityApi.since()
      .then(data => setActivity(data))
      .catch(() => setActivity(null))
      .finally(() => setActLoading(false));
    // Besuch registrieren – nur beim Öffnen des Activity-Tabs
    activityApi.seen().catch(() => {});
  }, [tab]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await actionsApi.sync();
      await load();
    } catch {
      setError('Sync fehlgeschlagen.');
    } finally {
      setSyncing(false);
    }
  };

  const handleComplete = async (id) => {
    try { await actionsApi.complete(id); await load(); } catch {}
  };
  const handleDismiss = async (id) => {
    try { await actionsApi.dismiss(id); await load(); } catch {}
  };
  const handleSnooze = async (id, snoozed_until) => {
    try { await actionsApi.snooze(id, snoozed_until); await load(); } catch {}
  };
  const handleReopen = async (id) => {
    try { await actionsApi.reopen(id); await load(); } catch {}
  };

  const tabStyle = (t) => ({
    padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    background: tab === t ? '#818cf8' : 'transparent',
    color: tab === t ? '#fff' : '#94a3b8',
    border: tab === t ? '1px solid #818cf8' : '1px solid transparent',
  });

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 16px 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 700, margin: 0 }}>Action Center</h1>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>Was ist jetzt wichtig?</div>
        </div>
        <button
          className="btn-primary"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Sync...' : 'Engines synchronisieren'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#450a0a', border: '1px solid #ef4444', color: '#fca5a5',
          borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Summary-Bar */}
      <SummaryBar summary={summary} />

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
          style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9',
            borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
          <option value="">Alle Prioritäten</option>
          <option value="CRITICAL">Kritisch</option>
          <option value="HIGH">Hoch</option>
          <option value="NORMAL">Normal</option>
          <option value="LOW">Niedrig</option>
        </select>
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
          style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9',
            borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
          <option value="">Alle Quellen</option>
          {Object.entries(SOURCE_BADGE).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={tabStyle('open')} onClick={() => setTab('open')}>Offen</button>
        <button style={tabStyle('completed')} onClick={() => setTab('completed')}>Erledigt</button>
        <button style={tabStyle('dismissed')} onClick={() => setTab('dismissed')}>Verworfen</button>
        <button style={tabStyle('activity')} onClick={() => setTab('activity')}>Aktivität</button>
      </div>

      {/* Inhalt */}
      {tab === 'activity' ? (
        <ActivityTab activity={activity} loading={actLoading} />
      ) : loading ? (
        <div style={{ color: '#94a3b8', padding: 20 }}>Lade Actions...</div>
      ) : actions.length === 0 ? (
        <div style={{ ...card, color: '#94a3b8', textAlign: 'center', padding: 32 }}>
          {tab === 'open' ? (
            <>
              <div style={{ fontSize: 28, marginBottom: 8 }}>Keine offenen Actions</div>
              <div style={{ fontSize: 13 }}>
                Drücken Sie "Engines synchronisieren" um Radar, Posteingang und Optimierung zu prüfen.
              </div>
            </>
          ) : (
            <div>Keine {STATUS_LABELS[tab === 'completed' ? 'COMPLETED' : 'DISMISSED']} Actions.</div>
          )}
        </div>
      ) : (
        <div>
          {actions.map(action => (
            <ActionCard
              key={action.id}
              action={action}
              onComplete={handleComplete}
              onDismiss={handleDismiss}
              onSnooze={handleSnooze}
              onReopen={handleReopen}
              onNavigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
